import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { MessageSquare, Send, AlertCircle, RefreshCw, Paperclip, X, Download, FileText, Image, Wifi, WifiOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Try to import WebSocket context
let useWebSocketContext = null;
try {
  const wsContext = require('../../context/WebSocketContext');
  useWebSocketContext = wsContext.useWebSocketContext;
} catch (e) {
  console.log('WebSocket context not available for messaging');
}

export default function MessagingCenter({ userRole = 'patient' }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Try to use WebSocket context
  let wsContext = null;
  try {
    if (useWebSocketContext) {
      wsContext = useWebSocketContext();
    }
  } catch (e) {
    // Not inside WebSocket provider
  }

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
    loadUnreadCount();
    
    // Poll for updates every 30 seconds (fallback)
    const interval = setInterval(() => {
      loadUnreadCount();
      if (selectedConversation) {
        loadMessages(selectedConversation.id, true);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Handle real-time messages from WebSocket
  useEffect(() => {
    if (wsContext?.newMessages?.length > 0) {
      wsContext.newMessages.forEach(msgData => {
        if (selectedConversation && msgData.conversation_id === selectedConversation.id) {
          // Add message to current conversation
          setMessages(prev => {
            // Check if message already exists
            if (prev.some(m => m.id === msgData.message.id)) {
              return prev;
            }
            return [...prev, msgData.message];
          });
          wsContext.clearNewMessage(msgData.message.id);
          scrollToBottom();
        } else {
          // Refresh conversations list for unread count
          loadConversations();
        }
      });
    }
  }, [wsContext?.newMessages, selectedConversation, scrollToBottom]);

  // Handle typing indicators from WebSocket
  useEffect(() => {
    if (wsContext && selectedConversation) {
      const typing = wsContext.getTypingUsers(selectedConversation.id);
      setTypingUsers(typing);
    }
  }, [wsContext?.typingUsers, selectedConversation]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load conversations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }
  };

  const loadMessages = async (conversationId, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/conversations/${conversationId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
      setError('');
      
      // Mark as read via WebSocket if available
      if (wsContext) {
        wsContext.markAsRead(conversationId);
      }
      
      // Refresh conversations to update unread counts
      await loadConversations();
      await loadUnreadCount();
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'application/pdf', 
                         'application/msword', 
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                         'application/vnd.ms-excel',
                         'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      const isValidType = validTypes.includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      
      if (!isValidType) {
        setError('Invalid file type. Allowed: JPG, PNG, PDF, DOCX, XLSX');
        return false;
      }
      if (!isValidSize) {
        setError('File too large. Maximum size: 5MB');
        return false;
      }
      return true;
    });
    
    setSelectedFiles([...selectedFiles, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    const token = localStorage.getItem('token');
    const uploadedAttachments = [];
    
    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(
          `${API_URL}/api/messages/upload`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        uploadedAttachments.push(response.data);
      } catch (err) {
        console.error('File upload failed:', err);
        throw err;
      }
    }
    
    return uploadedAttachments;
  };

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (wsContext && selectedConversation) {
      wsContext.sendTypingIndicator(selectedConversation.id, true);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        if (wsContext && selectedConversation) {
          wsContext.sendTypingIndicator(selectedConversation.id, false);
        }
      }, 2000);
    }
  }, [wsContext, selectedConversation]);

  const sendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedConversation) return;

    try {
      setSending(true);
      setUploading(true);
      const token = localStorage.getItem('token');
      
      // Upload files first if any
      let attachments = [];
      if (selectedFiles.length > 0) {
        attachments = await uploadFiles();
      }
      
      // Try to send via WebSocket first for real-time delivery
      if (wsContext?.isConnected) {
        const sent = wsContext.sendChatMessage(
          selectedConversation.id,
          newMessage || '(attachment)',
          attachments,
          false
        );
        
        if (sent) {
          setNewMessage('');
          setSelectedFiles([]);
          // Stop typing indicator
          wsContext.sendTypingIndicator(selectedConversation.id, false);
          return;
        }
      }
      
      // Fallback to HTTP if WebSocket not available
      await axios.post(
        `${API_URL}/api/conversations/${selectedConversation.id}/messages`,
        { 
          message: newMessage || '(attachment)', 
          is_urgent: false,
          attachments: attachments
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewMessage('');
      setSelectedFiles([]);
      await loadMessages(selectedConversation.id);
    } catch (err) {
      setError('Failed to send message');
      console.error(err);
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const startConversation = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get member info for patient
      const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const memberResponse = await axios.get(
        `${API_URL}/api/members/user/${userResponse.data.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const response = await axios.post(
        `${API_URL}/api/conversations`,
        {
          member_id: memberResponse.data.id,
          subject: 'New Conversation',
          initial_message: 'Hello, I need assistance.'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await loadConversations();
      setSelectedConversation(response.data);
      await loadMessages(response.data.id);
    } catch (err) {
      setError('Failed to start conversation');
      console.error(err);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-6 w-6 text-teal-600" />
          <h2 className="text-2xl font-bold">Messages</h2>
          {unreadCount > 0 && (
            <Badge variant="destructive" className="ml-2">
              {unreadCount} New
            </Badge>
          )}
          {/* WebSocket connection indicator */}
          {wsContext && (
            <span className="flex items-center text-xs ml-2">
              {wsContext.isConnected ? (
                <span className="flex items-center text-green-600">
                  <Wifi className="h-4 w-4 mr-1" />
                  Live
                </span>
              ) : (
                <span className="flex items-center text-gray-400">
                  <WifiOff className="h-4 w-4 mr-1" />
                  Offline
                </span>
              )}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              loadConversations();
              if (selectedConversation) {
                loadMessages(selectedConversation.id);
              }
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {userRole === 'patient' && conversations.length === 0 && (
            <Button onClick={startConversation}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Start Conversation
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden">
        {/* Conversations List */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Conversations</CardTitle>
            <CardDescription>
              {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {loading && conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No conversations yet</p>
                  {userRole === 'patient' && (
                    <Button onClick={startConversation} className="mt-4" size="sm">
                      Start a conversation
                    </Button>
                  )}
                </div>
              ) : (
                conversations.map((conv) => {
                  const isUnread = userRole === 'patient' 
                    ? conv.unread_count_patient > 0 
                    : conv.unread_count_staff > 0;
                  const unreadCountConv = userRole === 'patient'
                    ? conv.unread_count_patient
                    : conv.unread_count_staff;
                  const isOnline = wsContext?.isUserOnline(conv.patient_id);

                  return (
                    <div
                      key={conv.id}
                      onClick={() => {
                        setSelectedConversation(conv);
                        loadMessages(conv.id);
                      }}
                      className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-teal-50 border-l-4 border-l-teal-600' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-medium ${isUnread ? 'font-bold' : ''}`}>
                            {userRole === 'patient' ? 'Clinic Staff' : conv.patient_name}
                          </h3>
                          {isOnline && (
                            <span className="w-2 h-2 bg-green-500 rounded-full" title="Online"></span>
                          )}
                          {isUnread && (
                            <Badge variant="default" className="text-xs">
                              {unreadCountConv}
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatTime(conv.last_message_at)}
                        </span>
                      </div>
                      {conv.subject && (
                        <p className="text-sm text-gray-600 mb-1">{conv.subject}</p>
                      )}
                      <p className={`text-sm text-gray-500 truncate ${isUnread ? 'font-semibold text-gray-700' : ''}`}>
                        {conv.last_message}
                      </p>
                    </div>
                  );
                })
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Messages */}
        <Card className="col-span-1 md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">
              {selectedConversation
                ? userRole === 'patient'
                  ? 'Conversation with Clinic'
                  : `Conversation with ${selectedConversation.patient_name}`
                : 'Select a conversation'}
            </CardTitle>
            {selectedConversation?.subject && (
              <CardDescription>{selectedConversation.subject}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {!selectedConversation ? (
              <div className="h-[400px] flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p>Select a conversation to view messages</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col h-[500px]">
                {/* Messages Area */}
                <ScrollArea className="flex-1 pr-4 mb-4">
                  <div className="space-y-4">
                    {messages.map((msg) => {
                      const isOwnMessage = msg.sender_role === userRole;
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg p-3 ${
                              isOwnMessage
                                ? 'bg-teal-600 text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium opacity-90">
                                {msg.sender_name}
                              </span>
                              {msg.is_urgent && (
                                <Badge variant="destructive" className="text-xs">
                                  Urgent
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                            
                            {/* Attachments */}
                            {msg.attachments && msg.attachments.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {msg.attachments.map((att, idx) => (
                                  <a
                                    key={idx}
                                    href={`${API_URL}/api/messages/download/${att.file_path.split('/')[0]}/${att.file_path.split('/').pop()}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-2 p-2 rounded ${
                                      isOwnMessage ? 'bg-teal-700' : 'bg-gray-200'
                                    } hover:opacity-80 transition-opacity`}
                                  >
                                    {att.file_type?.startsWith('image/') ? (
                                      <Image className="h-4 w-4" />
                                    ) : (
                                      <FileText className="h-4 w-4" />
                                    )}
                                    <span className="text-xs flex-1 truncate">{att.filename}</span>
                                    <Download className="h-3 w-3" />
                                  </a>
                                ))}
                              </div>
                            )}
                            
                            <span className="text-xs opacity-75 mt-1 block">
                              {formatTime(msg.created_at)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Typing indicator */}
                {typingUsers.length > 0 && (
                  <div className="text-xs text-gray-500 italic mb-2">
                    Someone is typing...
                  </div>
                )}

                <Separator className="my-4" />

                {/* Selected Files Preview */}
                {selectedFiles.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs">
                        <Paperclip className="h-3 w-3" />
                        <span className="max-w-[100px] truncate">{file.name}</span>
                        <button onClick={() => removeFile(idx)} className="hover:text-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Message Input */}
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Textarea
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      className="min-h-[80px]"
                      disabled={sending}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      accept="image/jpeg,image/png,application/pdf,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => document.getElementById('file-upload').click()}
                      disabled={sending}
                      title="Attach files"
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={sendMessage}
                      disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending}
                      size="icon"
                    >
                      {uploading ? '...' : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {uploading ? 'Uploading files...' : 'Press Enter to send, Shift+Enter for new line. Max 5MB per file.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
